import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SITE_URL, OG_IMAGE, LANGS, DEFAULT_LANG } from "@/lib/seo";
import { localizedPath } from "@/lib/lang";
import { useSettings } from "@/context/SettingsContext";

const LOCALE_MAP = { nl: "nl_BE", en: "en_US", fr: "fr_BE", de: "de_DE" };

// Reusable SEO/GEO head manager. Sets title, description, canonical,
// hreflang alternates (nl/en/fr + x-default), Open Graph, Twitter cards
// and any JSON-LD structured data passed in `schemas`.
export const Seo = ({
  title,
  description,
  image = OG_IMAGE,
  type = "website",
  noindex = false,
  noAlternates = false,
  schemas = [],
}) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const settings = useSettings()?.settings;
  const lang = (i18n.language || DEFAULT_LANG).split("-")[0];
  // Self-referencing canonical for THIS language variant (path-based: nl=clean, en/fr/de prefixed).
  const canonical = `${SITE_URL}${localizedPath(pathname, lang)}`;

  // Admin-managed per-page, per-language overrides (Admin → SEO tab) take precedence.
  const override = settings?.seo?.[pathname] || {};
  const ovTitle = (override.title?.[lang] || "").trim();
  const ovDesc = (override.description?.[lang] || "").trim();
  // An overridden title is used verbatim (full control incl. branding); otherwise auto-suffix.
  const fullTitle = ovTitle || (title ? `${title} · KANN` : "KANN — Smart Home Accessories");
  const metaDescription = ovDesc || description;

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      {metaDescription && <meta name="description" content={metaDescription} />}
      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}

      {/* hreflang alternates — one per language (path-based: nl=clean, en/fr/de prefixed)
          + x-default -> Dutch. Reciprocal & self-referencing. Skipped on noindex pages and
          on single-language pages (e.g. blog articles) via noAlternates, matching the prerenderer. */}
      {!noindex && !noAlternates &&
        LANGS.map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={`${SITE_URL}${localizedPath(pathname, l)}`} />
        ))}
      {!noindex && !noAlternates && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${localizedPath(pathname, DEFAULT_LANG)}`} />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content="KANN" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {metaDescription && <meta property="og:description" content={metaDescription} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:locale" content={LOCALE_MAP[lang] || "en_US"} />
      {LANGS.filter((l) => l !== lang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={LOCALE_MAP[l]} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {metaDescription && <meta name="twitter:description" content={metaDescription} />}
      <meta name="twitter:image" content={image} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

export { SITE_URL };
